import React, { createRef, Component } from 'react';
import { observeRise } from '../utils/reveal.js';
import { buildPath } from '../utils/route_utils.js';

import '../css/TestimonialSection.css';

/**
 * TestimonialSection — "Client Success Stories" as two full-bleed panels.
 * The title sits left-aligned inside the bleed padding; the two stories
 * are equal panels side by side across the viewport, separated by a 1 px
 * hairline gap (they stack at <= 780). Reveal via .rise + observeRise
 * (honours prefers-reduced-motion through App.css / reveal.js).
 *
 * Props: lang ('zh' | 'en' | 'ja').
 */

class TestimonialSection extends Component {
  constructor(props) {
    super(props);
    this.state = {
      lang: this.props.lang,
      sectionTitle: {
        "zh": "客戶實績",
        "en": "Client Success Stories",
        "ja": "導入事例"
      },
      challengeLabel: {
        "zh": "挑戰",
        "en": "Challenge",
        "ja": "課題"
      },
      testimonials: [
        {
          id: "chem",
          logo: "/logo/chem_bolt_white.png",
          logoMono: true,
          logoWhiteBg: true,
          company: {
            "zh": "台灣大型重電廠商",
            "en": "A major heavy electrical manufacturer in Taiwan",
            "ja": "台湾の大手重電メーカー"
          },
          industry: {
            "zh": "電力設備製造",
            "en": "Power Equipment Manufacturing",
            "ja": "電力機器製造"
          },
          challenge: {
            "zh": "專業焊工嚴重短缺，工件誤差大，傳統對位系統複雜且缺乏彈性，產線效率受限。",
            "en": "Severe shortage of skilled welders, high workpiece variance, and complex, inflexible legacy alignment systems limiting production line efficiency.",
            "ja": "熟練溶接工の深刻な不足、ワーク誤差の大きさ、従来の位置合わせシステムの複雑さと柔軟性の欠如が、生産ライン効率を制限。"
          },
          quote: {
            "zh": "「導入 Nexuni 智慧焊接系統後，設定時間大幅縮減，一般操作人員也能輕鬆上手，不再依賴稀缺的專業焊工。即時誤差修正讓我們無需治具也能達到高精度。」",
            "en": "\"After deploying Nexuni's intelligent welding system, setup time dropped dramatically. Regular operators can now handle what previously required expert welders, and real-time error correction delivers high precision without fixtures.\"",
            "ja": "「Nexuni のスマート溶接システム導入後、セットアップ時間が大幅に短縮され、一般オペレーターでも専門溶接工なしで作業が可能になりました。リアルタイム誤差補正により、治具なしでも高精度を実現しています。」"
          },
          stats: [
            {
              value: "80%",
              label: {
                "zh": "設定時間減少",
                "en": "Setup Time Reduced",
                "ja": "セットアップ時間短縮"
              }
            },
            {
              value: "50%",
              label: {
                "zh": "生產效率提升",
                "en": "Production Efficiency Up",
                "ja": "生産効率向上"
              }
            },
            {
              value: "0",
              label: {
                "zh": "所需專業焊工",
                "en": "Expert Welders Needed",
                "ja": "必要な専門溶接工"
              }
            }
          ]
        },
        {
          id: "cbm",
          logo: "/logo/logowall_cbm.svg",
          logoWhiteBg: true,
          company: {
            "zh": "CBM",
            "en": "CBM",
            "ja": "CBM"
          },
          industry: {
            "zh": "綜合設施管理（新加坡）",
            "en": "Integrated Facility Management (Singapore)",
            "ja": "総合施設管理（シンガポール）"
          },
          challenge: {
            "zh": "新加坡法規要求大量人力進行實體巡邏與駐點，人力成本高昂且巡邏效率有限，傳統監控缺乏智慧分析能力。",
            "en": "Singapore regulations require heavy manpower for physical patrols and static guard posts, resulting in high labor costs, limited patrol efficiency, and legacy surveillance lacking intelligent analytics.",
            "ja": "シンガポールの規制により大量の人員が物理巡回と常駐に必要で、人件費が高騰し巡回効率も限られ、従来の監視システムにはインテリジェント分析機能が欠如。"
          },
          quote: {
            "zh": "「Nexuni 的 AI 影像保全系統與自主巡檢機器狗，讓我們的巡邏與監控全面升級。自動偵測越線、入侵事件後，人力需求大幅降低，每年節省的人力支出以億元計。自動生成的報告更讓管理層能即時掌握場域安全狀況。」",
            "en": "\"Nexuni's AI security system and Autonomous Inspection Quadruped transformed our patrol and surveillance operations. Automated intrusion and breach detection drastically reduced manpower needs, saving hundreds of millions annually. Auto-generated reports give management real-time visibility into site security.\"",
            "ja": "「Nexuni の AI セキュリティシステムと自律巡回四足ロボットにより、巡回・監視業務が全面的にアップグレードされました。自動的な越境・侵入検知により人員ニーズが大幅に減少し、年間の人件費を億単位で削減。自動生成レポートにより、管理層がリアルタイムで施設の安全状況を把握できるようになりました。」"
          },
          stats: [
            {
              value: "65%",
              label: {
                "zh": "人力需求減少",
                "en": "Manpower Reduced",
                "ja": "人員削減"
              }
            },
            {
              value: "55%",
              label: {
                "zh": "營運成本降低",
                "en": "Operating Costs Down",
                "ja": "運営コスト削減"
              }
            },
            {
              value: "24/7",
              label: {
                "zh": "AI 智慧監控",
                "en": "AI Surveillance",
                "ja": "AI 監視"
              }
            }
          ]
        }
      ]
    };

    this.rootRef = createRef();
    this.disposeRise = null;
  }

  componentDidMount() {
    this.disposeRise = observeRise(this.rootRef.current);
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.lang !== this.state.lang) {
      this.setState({ lang: nextProps.lang });
    }
  }

  componentWillUnmount() {
    if (this.disposeRise) this.disposeRise();
  }

  render() {
    const lang = this.state.lang;

    const caseLink = { zh: '閱讀案例', en: 'Read the case study', ja: '事例を読む' };

    return (
      <section className="testimonial-section" ref={this.rootRef} aria-labelledby="testimonial-section-title">
        {/* Abstract field: the NIRO point cloud, dimmed, dense beside the heading and gone under the cards. */}
        <div className="testimonial-bg" aria-hidden="true">
          <img src="/assets/niro/niro_field.jpg" alt="" loading="lazy" decoding="async" />
          <span className="testimonial-bg-scrim"></span>
        </div>
        <div className="testimonial-head">
          <h2 className="testimonial-section-title h-display rise" id="testimonial-section-title">
            {this.state.sectionTitle[lang]}
          </h2>
        </div>

        <div className="testimonial-panels">
          {this.state.testimonials.map((t, index) => (
            <article className="testimonial-panel" key={t.id} aria-labelledby={"testimonial-" + t.id + "-name"}>
              <div className="testimonial-panel-inner rise" style={{ transitionDelay: (index * 80) + 'ms' }}>
                {/* Header: logo plate + company info */}
                <div className="testimonial-panel-header">
                  <div className={"testimonial-logo-wrap" + (t.logoWhiteBg && !t.logoMono ? " testimonial-logo-white" : "") + (t.logoMono ? " testimonial-logo-mono" : "")}>
                    <img src={t.logo} alt={t.company[lang]} className="testimonial-logo" />
                  </div>
                  <div className="testimonial-company-info">
                    <h3 className="testimonial-company-name" id={"testimonial-" + t.id + "-name"}>{t.company[lang]}</h3>
                    <span className="testimonial-industry">{t.industry[lang]}</span>
                  </div>
                </div>

                {/* Stats: three values on a mustard top rule */}
                <div className="testimonial-stats">
                  {t.stats.map((stat, si) => (
                    <div className="testimonial-stat" key={si}>
                      <span className="testimonial-stat-value">{stat.value}</span>
                      <span className="testimonial-stat-label">{stat.label[lang]}</span>
                    </div>
                  ))}
                </div>

                {/* Challenge */}
                <div className="testimonial-challenge">
                  <span className="testimonial-challenge-label">{this.state.challengeLabel[lang]}</span>
                  <p>{t.challenge[lang]}</p>
                </div>

                {/* Quote */}
                <blockquote className="testimonial-quote">
                  {t.quote[lang]}
                </blockquote>
                <a className="testimonial-link btn-link" href={buildPath(lang, '/stories')} onClick={(e) => { e.preventDefault(); if (this.props.navigate) this.props.navigate('/stories'); else window.location.href = buildPath(lang, '/stories'); }}>
                  {caseLink[lang]}<span className="arrow" aria-hidden="true">→</span>
                </a>
              </div>
            </article>
          ))}
        </div>
      </section>
    );
  }
}

export default TestimonialSection;
